import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Permission, UsersFromOrganisationDb } from '../../api/ester-b2b-api/private/users/types'
import {
  useUpdateUserMutation,
  useGetUsersFromOrganisationQuery,
  useDeleteUserMutation,
  usePostUsers,
  usePostUsersWithCvs,
} from '../../api/ester-b2b-api/private/users/userQueries'
import { Alert, AlertTypes } from '../../components/Alert'
import { OnClickButton } from '../../components/buttons/onclick/OnClickButton'
import { Close, SearchLight } from '../../components/icons'
import { ComponentLayout } from '../../components/layouts/ComponentLayout'
import { BodyLarge, Title2, Title4 } from '../../components/styles/Typography'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { useSelectIsUserSso, useSelectUserOrganisationId } from '../../state/selectors'
import { theme } from '../../theme/theme'
import { isMobile, isTablet } from '../../utils/devices'
import { Modal } from '../../components/modals/Modal'
import { EmployeesTable } from '../../components/employees/EmployeesTable'
import { AddEmployeeModal } from '../../components/AddEmployeeModal'

type AlertType = {
  message?: string
  type?: AlertTypes
}

export const Employees = () => {
  const { t } = useTranslation()
  const organisationId = useSelectUserOrganisationId()
  const isUserSso = useSelectIsUserSso()
  const [search, setSearch] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<UsersFromOrganisationDb>()
  const [showModal, setShowModal] = useState(false)

  const [alertState, setAlertState] = useState<AlertType>({})
  const width = useWindowWidth()
  const isMobileDevice = isMobile(width) || isTablet(width)

  const postUserWithCvsMutation = usePostUsersWithCvs()
  const postSingleUserMutation = usePostUsers()
  const { mutate: updateUserMutation } = useUpdateUserMutation()
  const { mutate: deleteUser } = useDeleteUserMutation()
  const { data: users = [], refetch: refetchGetUsersFromOrganisationQuery } = useGetUsersFromOrganisationQuery(
    organisationId || ''
  )

  const noOfCreatedUsers = postUserWithCvsMutation.data?.createdUsers.length
  const noOfExistingUsers = postUserWithCvsMutation.data?.alreadyExistingUsers.length

  const handleFileUploadSuccess = useCallback(() => {
    setShowModal(false)
    refetchGetUsersFromOrganisationQuery()
    setAlertState({
      message: `${noOfCreatedUsers ? `${t('admin.employees.employeesCreated', { count: noOfCreatedUsers })} ` : ''}${noOfExistingUsers ? t('admin.employees.employeesAlreadyExisting', { count: noOfExistingUsers }) : ''}`,
      type: 'success',
    })
  }, [noOfCreatedUsers, noOfExistingUsers, refetchGetUsersFromOrganisationQuery, t])

  const handleSingleUserSuccess = useCallback(() => {
    setShowModal(false)
    refetchGetUsersFromOrganisationQuery()

    if (postSingleUserMutation.isSuccess && postSingleUserMutation.data?.[0]) {
      const { firstName, lastName } = postSingleUserMutation.data[0]
      setAlertState({
        message: t('admin.employees.employeeAdded', { firstName, lastName }),
        type: 'success',
      })
    }
  }, [postSingleUserMutation, refetchGetUsersFromOrganisationQuery, t])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (alertState.message) {
      const timer = setTimeout(() => {
        setAlertState({
          message: undefined,
          type: undefined,
        })
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [alertState.message])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }

  const onDeleteUser = () => {
    if (selectedEmployee) {
      deleteUser(selectedEmployee.id, {
        onSuccess: () => {
          refetchGetUsersFromOrganisationQuery()
          setAlertState({
            message: t('admin.employees.userDeleted'),
            type: 'success',
          })
        },
        onError: (error) => {
          console.error(error) // eslint-disable-line no-console
          setAlertState({
            message: t('admin.employees.deleteUserError'),
            type: 'error',
          })
        },
        onSettled: () => {
          setOpenDialog(false)
        },
      })
    }
  }

  const onUpdateUser = (userId: string, update: { newPermission?: Permission; isManager?: boolean }) => {
    const isPermissionUpdate = update.newPermission !== undefined
    const isManagerUpdate = update.isManager !== undefined

    const newUserData = isPermissionUpdate
      ? { permission: update.newPermission }
      : isManagerUpdate
        ? { isManager: !update.isManager }
        : {}

    const successMessage = isPermissionUpdate
      ? update.newPermission === 'ADMIN'
        ? t('admin.employees.adminPermissionGranted')
        : t('admin.employees.adminPermissionRevoked')
      : isManagerUpdate
        ? update.isManager
          ? t('admin.employees.managerResponsibilityRemoved')
          : t('admin.employees.managerResponsibilityAdded')
        : ''

    updateUserMutation(
      { id: userId, newUserData },
      {
        onSuccess: () => {
          refetchGetUsersFromOrganisationQuery()
          setAlertState({
            message: successMessage,
            type: 'success',
          })
        },
        onError: (error) => {
          setAlertState({
            message: error.message || t('admin.employees.updateUserError'),
            type: 'error',
          })
        },
      }
    )
  }

  const searchIcon =
    search.length > 0 ? (
      <Close size={24} color={theme.color.grey5} />
    ) : (
      <SearchLight size={24} color={theme.color.grey5} />
    )

  const filteredEmployees = users.filter((employee) => {
    const searchLower = search.toLowerCase()
    const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase()
    const email = employee.email.toLowerCase()
    return fullName.includes(searchLower) || email.includes(searchLower)
  })

  return (
    <>
      <ComponentLayout>
        <div>
          <StyledTitle2>{t('admin.employees.title', { count: users.length })} </StyledTitle2>
          <BodyLarge>{t('admin.employees.description')}</BodyLarge>
        </div>

        <AlertWrapper>
          {alertState.message && <Alert message={alertState.message} type={alertState.type || 'success'} />}
        </AlertWrapper>
        <InputContainer $isMobile={isMobileDevice}>
          <SearchContainer>
            <SearchInput
              type="text"
              name="searchBar"
              placeholder={t('admin.employees.searchPlaceholder')}
              value={search}
              onChange={handleSearch}
            />
            {searchIcon}
          </SearchContainer>
          {!isUserSso && (
            <OnClickButton
              text={t('admin.employees.addEmployeeButton')}
              onClick={() => {
                postUserWithCvsMutation.reset()
                postSingleUserMutation.reset()
                setShowModal(true)
              }}
            />
          )}
        </InputContainer>

        <EmployeesTable
          onSelectedEmployee={setSelectedEmployee}
          openDialog={() => setOpenDialog(true)}
          employees={filteredEmployees}
          onUpdateUser={onUpdateUser}
        />
      </ComponentLayout>

      {openDialog && (
        <Modal onClose={() => setOpenDialog(false)}>
          <ModalFlexWrapper>
            <div>
              <StyledTitle4 style={{ marginBottom: '5px' }}>{t('admin.employees.deleteDialogTitle')}</StyledTitle4>
              <BodyLarge style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {t('admin.employees.deleteDialogUser', {
                  firstName: selectedEmployee?.firstName,
                  lastName: selectedEmployee?.lastName,
                })}
              </BodyLarge>
              <BodyLarge>{t('admin.employees.deleteDialogConfirmation')}</BodyLarge>
            </div>

            <OnClickButton secondary text={t('admin.employees.deleteDialogConfirm')} onClick={onDeleteUser} />
          </ModalFlexWrapper>
        </Modal>
      )}
      {showModal && !isUserSso && (
        <AddEmployeeModal
          onClose={() => {
            setShowModal(false)
          }}
          onFileUploadSuccess={handleFileUploadSuccess}
          onSingleUserSuccess={handleSingleUserSuccess}
          postUserMutation={postUserWithCvsMutation}
          postSingleUserMutation={postSingleUserMutation}
        />
      )}
    </>
  )
}

const StyledTitle2 = styled(Title2)`
  margin-bottom: ${theme.spacing.xsmall}px;
`

const SearchContainer = styled.div`
  background-color: ${theme.color.white};
  border-radius: 50px;
  box-shadow: 0px 0px 2px 0.5px rgba(0, 0, 0, 0.15);
  border: none;
  width: 330px;
  height: ${theme.spacing.xxlarge}px;
  display: flex;
  align-items: center;
  padding: 0 ${theme.spacing.small}px;
  &:focus-within {
    border: 1px solid ${theme.color.plumMid};
  }
  @media screen and (max-width: ${theme.breakpoint.xsmall}px) {
    width: 100%;
  }
`

const SearchInput = styled.input`
  width: 100%;
  font-size: ${theme.fontSize.small}px;
  font-family: ${theme.font.body};
  color: ${theme.color.grey4};
  height: 90%;
  border-radius: 50px;
  border: none;
  outline: none;
  &:focus {
    color: ${theme.color.grey5};
  }
`
const InputContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? theme.spacing.large : theme.spacing.mediumLarge)}px;
  button {
    margin-top: ${({ $isMobile }) => ($isMobile ? theme.spacing.medium : 0)}px;
    @media screen and (max-width: 450px) {
      width: 100%;
    }
    width: ${({ $isMobile }) => ($isMobile ? '330px' : '200px')};
  }
`

const ModalFlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  max-width: 500px;
  gap: ${theme.spacing.medium}px;
`

const StyledTitle4 = styled(Title4)`
  margin-bottom: ${theme.spacing.xsmall}px;
`
const AlertWrapper = styled.div`
  margin-top: ${theme.spacing.large}px;
  position: relative;
  height: 46px;
  overflow: hidden;
  margin-bottom: ${theme.spacing.mediumLarge}px;
`
